import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, X, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AgentCard, WorkflowCard } from "@/components/catalogue/Cards";
import { EmptyState } from "@/components/common/EmptyState";
import { useCatalogue } from "@/context/CatalogueContext";
import { api } from "@/lib/api";
import { targetName, targetPath } from "@/lib/catalogue";
import { formatDateTime } from "@/lib/format";
import { Clock, Star, Activity } from "lucide-react";

export const AnnouncementBanner = () => {
  const [ann, setAnn] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("fcg-banner-hidden"));
  useEffect(() => { api.get("/announcements").then(({ data }) => setAnn(data.find((a) => a.show_as_banner) || null)).catch(() => {}); }, []);
  if (!ann || hidden === ann.id) return null;
  return (
    <div className="mb-6 flex items-start gap-3 rounded-xl border border-action/50 bg-action/10 px-4 py-3" role="status" data-testid="announcement-banner">
      <Megaphone className="mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm"><p className="font-semibold">{ann.title}</p><p className="mt-0.5 line-clamp-2 text-muted-foreground">{ann.body}</p><Link to="/whats-new" className="mt-1 inline-block text-xs font-medium underline underline-offset-4">Read in What's new</Link></div>
      <button type="button" onClick={() => { sessionStorage.setItem("fcg-banner-hidden", ann.id); setHidden(ann.id); }} aria-label="Hide announcement" className="rounded-lg p-1 hover:bg-muted" data-testid="announcement-banner-close"><X className="!h-4 !w-4" /></button>
    </div>
  );
};

const Section = ({ title, children, testid }) => (<section data-testid={testid}><h2 className="mb-3 text-lg font-semibold">{title}</h2>{children}</section>);

export const PersonalLayer = () => {
  const { favourites, agentsByKey, workflowsByKey } = useCatalogue();
  const [runs, setRuns] = useState(null);
  const [act, setAct] = useState(null);
  useEffect(() => {
    api.get("/runs", { params: { limit: 5 } }).then(({ data }) => setRuns(data)).catch(() => setRuns([]));
    api.get("/home/activity").then(({ data }) => setAct(data)).catch(() => setAct({ runs_this_week: [], new_prompts: [], new_prompt_count: 0 }));
  }, []);
  const favs = favourites.map((id) => agentsByKey[id] ? { kind: "a", e: agentsByKey[id] } : workflowsByKey[id] ? { kind: "w", e: workflowsByKey[id] } : null).filter(Boolean);
  const potw = act?.prompt_of_week;
  return (
    <div className="mt-12 space-y-12">
      {potw && (
        <section className="card-elevated flex flex-col gap-3 p-6 md:flex-row md:items-start" data-testid="prompt-of-week">
          <Trophy className="shrink-0 text-action" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-muted-foreground">Prompt of the week · {potw.upvotes} upvotes · {potw.copies} uses · by {potw.author_name}</p>
            <h2 className="mt-1 text-base font-semibold">{potw.title}</h2>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{potw.text}</p>
          </div>
          <Button variant="secondary" asChild><Link to={potw.for_id !== "any" ? targetPath(potw.for_id, workflowsByKey) : "/prompts"} data-testid="prompt-of-week-open">{potw.for_id !== "any" ? `Use with ${targetName(potw.for_id, agentsByKey, workflowsByKey)}` : "Open the Prompt Hub"}</Link></Button>
        </section>
      )}
      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Recently used by you" testid="home-recent">
          {runs && runs.length === 0 ? <EmptyState icon={Clock} title="No runs yet" body="Your last five runs will appear here once you start a job." testid="home-recent-empty" className="py-8" /> : (
            <ul className="space-y-2">{(runs || []).map((r) => (
              <li key={r.id}><Link to={`${targetPath(r.target_id, workflowsByKey)}?run=${r.id}`} className="flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 text-sm transition-quick hover:bg-muted" data-testid={`home-recent-${r.id}`}>
                <span className="min-w-0"><span className="block truncate font-medium">{r.label}</span><span className="text-xs text-muted-foreground">{formatDateTime(r.started_at)} · {r.status}</span></span>
                <span className="text-xs font-medium">Open</span>
              </Link></li>
            ))}</ul>
          )}
        </Section>
        <Section title="Team activity this week" testid="home-activity">
          {act && act.runs_this_week.length === 0 && act.new_prompt_count === 0 ? <EmptyState icon={Activity} title="Quiet so far" body="Runs and new prompts from this week will show here." testid="home-activity-empty" className="py-8" /> : act && (
            <div className="space-y-4 rounded-xl border bg-card p-5">
              {act.runs_this_week.length > 0 && (
                <ul className="space-y-2">{act.runs_this_week.map((r) => {
                  const max = act.runs_this_week[0].count;
                  return (<li key={r.target_id} className="text-sm"><div className="flex justify-between"><span>{targetName(r.target_id, agentsByKey, workflowsByKey)}</span><span className="text-muted-foreground">{r.count} runs</span></div><div className="mt-1 h-1.5 rounded-full bg-muted"><div className="h-1.5 rounded-full bg-foreground/70" style={{ width: `${(r.count / max) * 100}%` }} /></div></li>);
                })}</ul>
              )}
              <p className="text-sm" data-testid="home-new-prompts">{act.new_prompt_count} new {act.new_prompt_count === 1 ? "prompt" : "prompts"} this week{act.new_prompts.length > 0 && `: ${act.new_prompts.map((p) => p.title).join(", ")}`}</p>
            </div>
          )}
        </Section>
      </div>
      <Section title="Favourites" testid="home-favourites">
        {favs.length === 0 ? <EmptyState icon={Star} title="No favourites yet" body="Star an agent or workflow to keep it one click away." testid="home-favourites-empty" className="py-8" /> : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{favs.map(({ kind, e }) => kind === "a" ? <AgentCard key={e.emp_no} agent={e} /> : <WorkflowCard key={e.id} workflow={e} />)}</div>
        )}
      </Section>
    </div>
  );
};
